import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from '../../node_modules/classnames/index.js';
import addDays from '../../node_modules/date-fns/esm/addDays/index.js';
import format from '../../node_modules/date-fns/esm/format/index.js';
import { throttle } from '../../node_modules/throttle-debounce/esm/index.js';
import { d as dist } from '../../node_modules/react-date-range/dist/index.js';
import modules_4d9b124f from '../base-date-range-picker.module.scss.js';

var _PropTypes$shape;
var defaultLabels = {
  back: "Back",
  prevButton: "Previous month",
  nextButton: "Next month"
};
var defaultResponsive = {
  default: {
    months: 1,
    vertical: true
  },
  768: {
    months: 2,
    vertical: false
  }
};

var DatePicker = function DatePicker(_ref) {
  var classNames = _ref.classNames,
      providedLabels = _ref.labels,
      locale = _ref.locale,
      responsive = _ref.responsive,
      minDate = _ref.minDate,
      maxDate = _ref.maxDate,
      onChange = _ref.onChange,
      date = _ref.date,
      setDate = _ref.setDate,
      monthDisplayFormat = _ref.monthDisplayFormat,
      dateFormat = _ref.dateFormat,
      renderBackIcon = _ref.renderBackIcon,
      renderFooterComponent = _ref.renderFooterComponent,
      selectedDayBackgroundColor = _ref.selectedDayBackgroundColor,
      reactCalendarProps = _ref.reactCalendarProps;

  var labels = _extends({}, defaultLabels, providedLabels);

  var containerRef = useRef();
  var headerRef = useRef();
  var footerRef = useRef();

  var _useState = useState(true),
      displayInfiniteCalendar = _useState[0],
      setDisplayInfiniteCalendar = _useState[1];

  var _useState2 = useState(2),
      monthsToDisplay = _useState2[0],
      setMonthsToDisplay = _useState2[1];

  var handleResponsiveView = throttle(150, function () {
    var settings = (responsive == null ? void 0 : responsive.default) || {
      months: 1,
      vertical: true
    };
    var sortedSettings = Object.entries(responsive).filter(function (_ref2) {
      var breakpoint = _ref2[0];
      return breakpoint !== "default";
    }).sort(function (a, b) {
      return b[0] - a[0];
    });

    for (var i = 0; i < sortedSettings.length; i++) {
      var _sortedSettings$i = sortedSettings[i],
          breakpoint = _sortedSettings$i[0],
          _setting = _sortedSettings$i[1];

      if (window.matchMedia("(min-width: " + breakpoint + "px)").matches) {
        settings = _setting;
        break;
      }
    }

    setMonthsToDisplay(settings.months || 1);
    setDisplayInfiniteCalendar(!!settings.vertical);
  });

  var _useState3 = useState(300),
      maxVerticalInfiniteCalendarHeight = _useState3[0],
      setMaxVerticalInfiniteCalendarHeight = _useState3[1];

  var calcInfiniteCalendarHeight = function calcInfiniteCalendarHeight(_ref3) {
    var _containerEl$target$q;

    var containerEl = _ref3[0],
        els = _ref3.slice(1);

    var containerInnerHeight = containerEl.contentBoxSize[0].blockSize;
    var totalHeaderFooterHeight = els.reduce(function (acc, el) {
      var height = acc + el.borderBoxSize[0].blockSize;
      return height;
    }, 0); // react-date-range does not include the weekdays heading in the heigh

    var weekDaysElHeight = (_containerEl$target$q = containerEl.target.querySelector(".rdrWeekDays")) == null ? void 0 : _containerEl$target$q.getBoundingClientRect().height; // Accounting for magic number 11 that is added by react-date-range

    setMaxVerticalInfiniteCalendarHeight(containerInnerHeight - totalHeaderFooterHeight - weekDaysElHeight - 11);
  };

  useEffect(function () {
    handleResponsiveView();
    var resizeObserver = new ResizeObserver(calcInfiniteCalendarHeight);
    resizeObserver.observe(containerRef.current);
    resizeObserver.observe(headerRef.current);

    if (footerRef.current) {
      resizeObserver.observe(footerRef.current);
    }

    window.addEventListener("resize", handleResponsiveView);
    return function () {
      window.removeEventListener("resize", handleResponsiveView);
      resizeObserver.disconnect();
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responsive]);
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: classnames(modules_4d9b124f.container, classNames == null ? void 0 : classNames.calendarContainer),
    "data-rdr-direction": displayInfiniteCalendar ? "vertical" : "horizontal"
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames(modules_4d9b124f.wrapper, classNames == null ? void 0 : classNames.calendarWrapper)
  }, /*#__PURE__*/React.createElement("div", {
    ref: headerRef,
    className: classnames(classNames == null ? void 0 : classNames.calendarHeader)
  }, renderBackIcon({
    className: classNames == null ? void 0 : classNames.calendarBackIcon,
    labels: {
      back: labels.back
    },
    onClick: function onClick() {
      onChange(date);
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: classnames(classNames == null ? void 0 : classNames.mobileDateInputContainer)
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    className: classnames(classNames == null ? void 0 : classNames.selectedDate)
  }, format(date, dateFormat, {
    locale: locale
  })))), /*#__PURE__*/React.createElement(dist.Calendar, _extends({
    onChange: function onChange(item) {
      return setDate(item);
    },
    locale: locale,
    date: date,
    minDate: minDate,
    maxDate: maxDate,
    preventSnapRefocus: true,
    showDateDisplay: false,
    months: monthsToDisplay,
    monthDisplayFormat: monthDisplayFormat,
    direction: displayInfiniteCalendar ? "vertical" : "horizontal",
    scroll: {
      enabled: displayInfiniteCalendar,
      calendarHeight: maxVerticalInfiniteCalendarHeight
    },
    color: selectedDayBackgroundColor
  }, reactCalendarProps)), renderFooterComponent == null ? void 0 : renderFooterComponent({
    ref: footerRef,
    date: date,
    onClick: function onClick() {
      onChange(date);
    }
  })));
};

DatePicker.propTypes = {
  classNames: PropTypes.shape({
    calendarContainer: PropTypes.string,
    calendarWrapper: PropTypes.string,
    calendarHeader: PropTypes.string,
    calendarBackIcon: PropTypes.string,
    singleCalendar: PropTypes.string,
    mobileDateInputContainer: PropTypes.string,
    selectedDate: PropTypes.string
  }),
  labels: PropTypes.shape({
    back: PropTypes.string,
    prevButton: PropTypes.string,
    nextButton: PropTypes.string
  }),

  /**
   * Import supported locale from https://github.com/date-fns/date-fns/tree/main/src/locale
   * `import { enCA, frCA } from "date-fns/locale";`
   */
  locale: PropTypes.object.isRequired,
  responsive: PropTypes.shape((_PropTypes$shape = {
    default: PropTypes.shape({
      months: PropTypes.number,
      vertical: PropTypes.bool
    })
  }, _PropTypes$shape[PropTypes.number] = PropTypes.shape({
    months: PropTypes.number,
    vertical: PropTypes.bool
  }), _PropTypes$shape)),
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  date: PropTypes.instanceOf(Date),
  setDate: PropTypes.func.isRequired,
  monthDisplayFormat: PropTypes.string,
  dateFormat: PropTypes.string,
  selectedDayBackgroundColor: PropTypes.string,
  renderBackIcon: PropTypes.func,
  renderFooterComponent: PropTypes.func,

  /**
   * Calendar props from react-date-range https://hypeserver.github.io/react-date-range/#calendar
   */
  reactCalendarProps: PropTypes.object
};
DatePicker.defaultProps = {
  classNames: undefined,
  labels: defaultLabels,
  responsive: defaultResponsive,
  onChange: undefined,
  date: addDays(new Date(), 1),
  minDate: addDays(new Date(), 1),
  maxDate: addDays(new Date(), 365),
  monthDisplayFormat: "MMMM yyyy",
  dateFormat: "iii, LLL dd",
  selectedDayBackgroundColor: "#f2f2f2",
  // eslint-disable-next-line react/prop-types
  renderBackIcon: function renderBackIcon(_ref4) {
    var className = _ref4.className,
        labels = _ref4.labels,
        onClick = _ref4.onClick;
    return /*#__PURE__*/React.createElement("button", {
      type: "button",
      "aria-hidden": "true",
      className: className,
      onClick: onClick
    }, labels.back);
  },
  renderFooterComponent: undefined,
  reactCalendarProps: undefined
}; // eslint-disable-next-line import/prefer-default-export

export { DatePicker };
//# sourceMappingURL=DatePicker.js.map
